import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaCrown, FaTrashAlt, FaKey } from 'react-icons/fa';

const UserList = ({ darkMode, showNotification }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', password: '', role: 'user' });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (!token || !storedUser) {
        showNotification('You need to log in first', 'error');
        navigate('/login');
        return;
      }

      const user = JSON.parse(storedUser);
      if (!user.roles.includes('admin')) {
        showNotification('Admin access required', 'error');
        navigate('/');
        return;
      }

      try {
        const response = await fetch('/auth/users', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data);
        } else {
          showNotification('Failed to fetch users. Please log in again.', 'error');
          navigate('/login');
        }
      } catch (error) {
        showNotification('Error fetching users', 'error');
      }
    };

    fetchUsers();
  }, [navigate, showNotification]);

  const openDeleteModal = (userId) => {
    setSelectedUser(userId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/auth/users/${selectedUser}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        setUsers(users.filter(user => user._id !== selectedUser));
        showNotification('User deleted successfully', 'success');
      } else {
        showNotification('Failed to delete user', 'error');
      }
    } catch (error) {
      showNotification('Error deleting user', 'error');
    }
    setShowDeleteModal(false);
  };

  const openRoleModal = (userId, currentRole) => {
    setSelectedUser(userId);
    setNewRole(currentRole === 'admin' ? 'user' : 'admin');
    setShowRoleModal(true);
  };

  const handleRoleChange = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/auth/users/${selectedUser}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });
      if (response.ok) {
        setUsers(users.map(user => user._id === selectedUser ? { ...user, role: newRole } : user));
        showNotification('User role updated successfully', 'success');
      } else {
        showNotification('Failed to update user role', 'error');
      }
    } catch (error) {
      showNotification('Error updating user role', 'error');
    }
    setShowRoleModal(false);
  };

  const openPasswordModal = (userId) => {
    setSelectedUser(userId);
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async () => {
    if (newPassword.trim() === '') {
      showNotification('Password cannot be empty', 'error');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/auth/users/${selectedUser}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });
      if (response.ok) {
        showNotification('Password reset successfully', 'success');
        setShowPasswordModal(false);
        setNewPassword('');
      } else {
        showNotification('Failed to reset password', 'error');
      }
    } catch (error) {
      showNotification('Error resetting password', 'error');
    }
  };

  const handleCreateUser = async () => {
    const { name, email, password } = newUserData;
    if (!name || !email || !password) {
      showNotification('Please fill all fields', 'error');
      return;
    }

    try {
      const response = await fetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUserData),
      });

      const data = await response.json();

      if (response.ok) {
        setUsers([...users, data]);
        showNotification('User created successfully', 'success');
        setShowCreateModal(false);
        setNewUserData({ name: '', email: '', password: '', role: 'user' });
      } else {
        showNotification(data.message || 'Failed to create user', 'error');
      }
    } catch (error) {
      showNotification('Error creating user', 'error');
    }
  };

  return (
    <div className={`max-w-4xl mx-auto p-6 rounded shadow-md ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}>
      <h2 className="text-2xl font-bold mb-4">User List</h2>

      <button onClick={() => setShowCreateModal(true)} className="mb-4 bg-green-600 text-white px-4 py-2 rounded">
        + Create User
      </button>

      <table className={`min-w-full ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}>
        <thead>
          <tr>
            <th className="py-2 px-4 text-left">Name</th>
            <th className="py-2 px-4 text-left">Email</th>
            <th className="py-2 px-4 text-left">Role</th>
            <th className="py-2 px-4 text-left">Created</th>
            <th className="py-2 px-4 text-left">Last Login</th>
            <th className="py-2 px-4 text-left">IP Address</th>
            <th className="py-2 px-4 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user._id} className={darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}>
              <td className="py-2 px-4 border-b">{user.name}</td>
              <td className="py-2 px-4 border-b">{user.email}</td>
              <td className="py-2 px-4 border-b">{user.role}</td>
              <td className="py-2 px-4 border-b">{new Date(user.created).toLocaleDateString()}</td>
              <td className="py-2 px-4 border-b">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}</td>
              <td className="py-2 px-4 border-b">{user.ip || 'N/A'}</td>
              <td className="py-2 px-4 border-b text-center space-x-2">
                <button onClick={() => openRoleModal(user._id, user.role)}>{user.role === 'admin' ? <FaCrown className="text-yellow-500" /> : <FaUser />}</button>
                <button onClick={() => openPasswordModal(user._id)}><FaKey /></button>
                <button onClick={() => openDeleteModal(user._id)}><FaTrashAlt className="text-red-500" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded shadow-md w-96">
            <h2 className="text-xl mb-4">Create New User</h2>
            <input type="text" placeholder="Name" value={newUserData.name} onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })} className={`p-2 border rounded w-full mb-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`} />
            <input type="email" placeholder="Email" value={newUserData.email} onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })} className={`p-2 border rounded w-full mb-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`} />
            <input type="password" placeholder="Password" value={newUserData.password} onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })} className={`p-2 border rounded w-full mb-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`} />
            <select value={newUserData.role} onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })} className={`p-2 border rounded w-full mb-4 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <div className="flex justify-end space-x-2">
              <button onClick={handleCreateUser} className="bg-green-600 text-white px-4 py-2 rounded">Create</button>
              <button onClick={() => setShowCreateModal(false)} className="bg-gray-500 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded shadow-md w-96">
            <h2 className="text-xl mb-4">Reset Password</h2>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" className={`p-2 border rounded w-full mb-4 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`} />
            <div className="flex justify-end space-x-2">
              <button onClick={handlePasswordSubmit} className="bg-blue-500 text-white px-4 py-2 rounded">Save</button>
              <button onClick={() => setShowPasswordModal(false)} className="bg-gray-500 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded shadow-md w-96">
            <h2 className="text-xl mb-4">Change Role</h2>
            <p className="mb-4">Change role to <strong>{newRole}</strong>?</p>
            <div className="flex justify-end space-x-2">
              <button onClick={handleRoleChange} className="bg-blue-500 text-white px-4 py-2 rounded">Confirm</button>
              <button onClick={() => setShowRoleModal(false)} className="bg-gray-500 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded shadow-md w-96">
            <h2 className="text-xl mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this user?</p>
            <div className="flex justify-end space-x-2">
              <button onClick={confirmDelete} className="bg-red-500 text-white px-4 py-2 rounded">Delete</button>
              <button onClick={() => setShowDeleteModal(false)} className="bg-gray-500 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
